import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const CustomReportBuilder = ({ isOpen, onClose, onSave }) => {
  const [reportConfig, setReportConfig] = useState({
    title: '',
    description: '',
    sections: [],
    layout: 'standard',
    includeCharts: true,
    includeTables: true,
    includeRecommendations: true
  });

  const availableSections = [
    { id: 'executive-summary', label: 'Executive Summary', icon: 'FileText', required: true },
    { id: 'net-worth', label: 'Net Worth Analysis', icon: 'TrendingUp' },
    { id: 'asset-allocation', label: 'Asset Allocation', icon: 'PieChart' },
    { id: 'income-analysis', label: 'Income Analysis', icon: 'DollarSign' },
    { id: 'expense-tracking', label: 'Expense Tracking', icon: 'Receipt' },
    { id: 'cash-flow', label: 'Cash Flow Projections', icon: 'BarChart3' },
    { id: 'retirement-readiness', label: 'Retirement Readiness', icon: 'Target' },
    { id: 'scenario-analysis', label: 'Scenario Analysis', icon: 'GitBranch' },
    { id: 'risk-assessment', label: 'Risk Assessment', icon: 'Shield' },
    { id: 'recommendations', label: 'Recommendations', icon: 'Lightbulb' }
  ];

  const layoutOptions = [
    { value: 'standard', label: 'Standard Layout' },
    { value: 'executive', label: 'Executive Summary Focus' },
    { value: 'detailed', label: 'Detailed Analysis' },
    { value: 'presentation', label: 'Presentation Style' }
  ];

  const handleSectionToggle = (sectionId) => {
    const section = availableSections.find(s => s.id === sectionId);
    if (section.required) return;

    setReportConfig(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionId)
        ? prev.sections.filter(id => id !== sectionId)
        : [...prev.sections, sectionId]
    }));
  };

  const handleSave = () => {
    const newReport = {
      id: `custom-${Date.now()}`,
      title: reportConfig.title || 'Custom Report',
      description: reportConfig.description || 'Custom generated report',
      icon: 'FileText',
      status: 'draft',
      lastGenerated: 'Never',
      dateRange: 'Custom',
      pages: Math.ceil(reportConfig.sections.length * 2.5),
      isCustom: true,
      config: reportConfig
    };
    
    onSave(newReport);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Plus" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">Custom Report Builder</h2>
              <p className="text-sm text-muted-foreground">Create a personalized financial report</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Report Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Report Title"
                  placeholder="Enter report title"
                  value={reportConfig.title}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
                <Select
                  label="Layout Style"
                  options={layoutOptions}
                  value={reportConfig.layout}
                  onChange={(value) => setReportConfig(prev => ({ ...prev, layout: value }))}
                />
              </div>
              <Input
                label="Description"
                placeholder="Brief description of the report purpose"
                value={reportConfig.description}
                onChange={(e) => setReportConfig(prev => ({ ...prev, description: e.target.value }))}
              />
            </section>

            {/* Section Selection */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Report Sections</h3>
              <p className="text-sm text-muted-foreground">
                Select the sections you want to include in your custom report
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableSections.map((section) => {
                  const isSelected = section.required || reportConfig.sections.includes(section.id);
                  const isRequired = section.required;
                  
                  return (
                    <div
                      key={section.id}
                      className={`
                        flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200
                        ${isSelected 
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                        }
                        ${isRequired ? 'opacity-75' : ''}
                      `}
                      onClick={() => !isRequired && handleSectionToggle(section.id)}
                    >
                      <div className={`
                        w-8 h-8 rounded-lg flex items-center justify-center
                        ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                      `}>
                        <Icon name={section.icon} size={16} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{section.label}</span>
                          {isRequired && (
                            <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                      {!isRequired && (
                        <div className={`
                          w-5 h-5 border-2 rounded flex items-center justify-center
                          ${isSelected 
                            ? 'border-primary bg-primary' :'border-border'
                          }
                        `}>
                          {isSelected && <Icon name="Check" size={12} className="text-primary-foreground" />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Display Options */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Display Options</h3>
              <div className="space-y-3">
                <Checkbox
                  label="Include Charts and Graphs"
                  description="Add visual representations of data"
                  checked={reportConfig.includeCharts}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, includeCharts: e.target.checked }))}
                />
                <Checkbox
                  label="Include Data Tables"
                  description="Add detailed numerical data tables"
                  checked={reportConfig.includeTables}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, includeTables: e.target.checked }))}
                />
                <Checkbox
                  label="Include Recommendations"
                  description="Add AI-powered insights and suggestions"
                  checked={reportConfig.includeRecommendations}
                  onChange={(e) => setReportConfig(prev => ({ ...prev, includeRecommendations: e.target.checked }))}
                />
              </div>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Selected sections: {reportConfig.sections.length + 1} (including Executive Summary)
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              disabled={!reportConfig.title.trim()}
              iconName="Save"
              iconPosition="left"
            >
              Save Report Template
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;